import React, { use, useCallback, useEffect, useRef, useState } from 'react'

import { useInterview } from '@/react-query/interview/interview'
import { useUserSettings } from '@/react-query/user-settings'
import { useBeforeUnload } from '@/views/hooks/useBeforeUnload'

import { InputTranscriptContext } from '../../components/FullTranscript/context/InputTranscriptContext'
import { useLiveTranscription } from '../../hooks/useLiveTranscription'

import { getSupportedMimeType, stopMediaStreamTracks } from './audioUtils'
import {
  deleteAudioChunks,
  getAudioChunks,
  getLastSequenceNumber,
  saveAudioChunk,
} from './indexed-db'
import {
  AudioDevice,
  AudioRecorderValue,
  LiveTranscriptionProvider,
  RecordingSource,
} from './types'
import { useErrorToast } from './useErrorToast'
import { usePcmStreaming } from './usePcmStreaming'

export const AudioRecorderContext = React.createContext<
  AudioRecorderValue | undefined
>(undefined)

interface StreamState {
  micStream: MediaStream | null
  tabStream: MediaStream | null
  visStream: MediaStream | null
  audioContext: AudioContext | null
  micSourceNode: MediaStreamAudioSourceNode | null
  tabSourceNode: MediaStreamAudioSourceNode | null
}

const INITIAL_STREAM_STATE: StreamState = {
  micStream: null,
  tabStream: null,
  visStream: null,
  audioContext: null,
  micSourceNode: null,
  tabSourceNode: null,
}

export const AudioRecorderProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { showErrorToast } = useErrorToast()
  const interviewId = useInterview().data?.id
  const userSettings = useUserSettings()
  const liveTranscriptionEnabled =
    userSettings.data?.enableLiveTranscription ?? false

  const supportedMimeType = getSupportedMimeType()
  const { setupPcmCapture, cleanupPcm } = usePcmStreaming()

  const [isRecording, setIsRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const chunkSequence = useRef(0)
  const [mimeType, setMimeType] = useState<string | null>(null)
  const [recordedFile, setRecordedFile] = useState<File | null>(null)
  const [sourceType, setSourceType] = useState<RecordingSource | null>(null)
  const [streams, setStreams] = useState<StreamState>(INITIAL_STREAM_STATE)
  const [availableDevices, setAvailableDevices] = useState<AudioDevice[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null)

  const sendAudioChunkRef = useRef<((chunk: ArrayBuffer) => void) | null>(null)

  const [
    selectedLiveTranscriptionProvider,
    setSelectedLiveTranscriptionProvider,
  ] = useState<LiveTranscriptionProvider>('deepgram')

  const handleLiveTranscript = useCallback(
    (transcript: string, isFinal: boolean) => {
      if (isFinal) {
        console.log('Live transcript (final):', transcript)
      }
    },
    [],
  )

  const liveTranscription = useLiveTranscription({
    interviewId: interviewId ?? '',
    onTranscript: handleLiveTranscript,
    preferredProvider: selectedLiveTranscriptionProvider,
  })

  useEffect(() => {
    sendAudioChunkRef.current = liveTranscription.sendAudioChunk
  }, [liveTranscription.sendAudioChunk])

  useBeforeUnload([isRecording])
  useCleanChunksIfFullTranscriptIsGenerated()
  const { startFileTranscription } = use(InputTranscriptContext)

  const cleanupStreams = useCallback(async () => {
    stopMediaStreamTracks(streams.visStream)
    streams.micSourceNode?.disconnect()
    streams.tabSourceNode?.disconnect()
    stopMediaStreamTracks(streams.micStream)
    stopMediaStreamTracks(streams.tabStream)

    if (streams.audioContext) {
      await streams.audioContext.close()
    }

    await cleanupPcm()
    liveTranscription.disconnect()

    setStreams(INITIAL_STREAM_STATE)
    setMimeType(null)
    setMediaRecorder(null)
    setSourceType(null)
    setIsRecording(false)
    setAvailableDevices([])
    setSelectedDeviceId(null)
    destinationRef.current = null
  }, [streams, cleanupPcm, liveTranscription])

  const startRecording = async ({
    inputStreams,
    isResume = false,
    initialDeviceId,
  }: {
    inputStreams: MediaStream[]
    isResume?: boolean
    initialDeviceId?: string
  }) => {
    if (!interviewId) return

    const audioContext = new AudioContext()
    const destination = audioContext.createMediaStreamDestination()
    destinationRef.current = destination

    const nodes = inputStreams.map((stream) => {
      const source = audioContext.createMediaStreamSource(stream)
      source.connect(destination)
      return source
    })

    setStreams({
      audioContext,
      micStream: inputStreams[0] ?? null,
      tabStream: inputStreams[1] ?? null,
      visStream: destination.stream,
      micSourceNode: nodes[0] ?? null,
      tabSourceNode: nodes[1] ?? null,
    })

    if (initialDeviceId) {
      setSelectedDeviceId(initialDeviceId)
    }

    if (isResume) {
      chunkSequence.current = (await getLastSequenceNumber(interviewId)) + 1
    } else {
      await deleteAudioChunks(interviewId)
      chunkSequence.current = 0
    }

    const recorder = supportedMimeType
      ? new MediaRecorder(destination.stream, { mimeType: supportedMimeType })
      : new MediaRecorder(destination.stream)

    recorder.ondataavailable = async (event) => {
      if (event.data.size > 0) {
        try {
          await saveAudioChunk(
            interviewId,
            event.data,
            chunkSequence.current++,
            sourceType || RecordingSource.MIC,
            mimeType || supportedMimeType,
            Date.now(),
          )
        } catch {
          showErrorToast('Failed to save audio')
        }
      }
    }

    setMimeType(recorder.mimeType)
    setMediaRecorder(recorder)
    recorder.start(1000)
    setIsRecording(true)

    if (liveTranscriptionEnabled) {
      const provider = await liveTranscription.connect()
      const useMultichannel =
        provider === 'assemblyai' && inputStreams.length > 1
      const sendChunk = sendAudioChunkRef.current
      if (sendChunk) {
        await setupPcmCapture(inputStreams, useMultichannel, sendChunk)
      }
    }
  }

  const enumerateAudioDevices = async (): Promise<AudioDevice[]> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${index + 1}`,
        }))
      setAvailableDevices(audioInputs)
      return audioInputs
    } catch (error) {
      console.error('Failed to enumerate audio devices:', error)
      return []
    }
  }

  const startMicrophoneRecording = async ({ isResume = false } = {}) => {
    if (!interviewId) return
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      })
      const audioTrack = micStream.getAudioTracks()[0]
      const settings = audioTrack.getSettings()
      const deviceId = settings.deviceId || null
      setSelectedDeviceId(deviceId)
      setSourceType(RecordingSource.MIC)
      await startRecording({
        inputStreams: [micStream],
        isResume,
        initialDeviceId: deviceId ?? undefined,
      })
      await enumerateAudioDevices()
    } catch (error) {
      showErrorToast('Failed to start microphone recording')
      console.error(error)
      await cleanupStreams()
    }
  }

  const startTabRecording = async ({ isResume = false } = {}) => {
    if (!interviewId) return
    try {
      const [micStream, tabStream] = await Promise.all([
        navigator.mediaDevices.getUserMedia({ audio: {} }),
        navigator.mediaDevices.getDisplayMedia({ audio: true, video: true }),
      ])
      const audioTrack = micStream.getAudioTracks()[0]
      const settings = audioTrack.getSettings()
      const deviceId = settings.deviceId || null
      setSelectedDeviceId(deviceId)
      setSourceType(RecordingSource.TAB)
      await startRecording({
        inputStreams: [micStream, tabStream],
        isResume,
        initialDeviceId: deviceId ?? undefined,
      })
      await enumerateAudioDevices()
    } catch (error) {
      showErrorToast('Failed to start tab recording')
      console.error(error)
      await cleanupStreams()
    }
  }

  const switchMicrophone = async (deviceId: string) => {
    if (!streams.audioContext || !destinationRef.current) return

    try {
      const newMicStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      })

      if (streams.micSourceNode) {
        streams.micSourceNode.disconnect()
      }
      if (streams.micStream) {
        streams.micStream.getTracks().forEach((track) => track.stop())
      }

      const newSourceNode =
        streams.audioContext.createMediaStreamSource(newMicStream)
      newSourceNode.connect(destinationRef.current)

      setStreams((prev) => ({
        ...prev,
        micStream: newMicStream,
        micSourceNode: newSourceNode,
      }))
      setSelectedDeviceId(deviceId)
    } catch (error) {
      showErrorToast('Failed to switch microphone')
      console.error(error)
    }
  }

  const uploadExistingRecording = useCallback(async () => {
    if (!interviewId) return

    try {
      const record = await getAudioChunks(interviewId)
      const recordMimeType = record?.mimeType || 'audio/webm;codecs=opus'
      const blob = new Blob(record?.chunks || [], { type: recordMimeType })
      const source = record?.source || RecordingSource.MIC
      const file = new File(
        [blob],
        `Live ${source === RecordingSource.TAB ? 'Tab' : 'Mic'} Recording`,
        { type: blob.type },
      )
      setRecordedFile(file)
      await startFileTranscription({ file, source, interviewId })
    } catch {
      showErrorToast('Failed to upload recording')
    }
  }, [interviewId, startFileTranscription, showErrorToast])

  const stopRecordingAndUpload = useCallback(async () => {
    if (!interviewId) return

    setIsRecording(false)
    if (!mediaRecorder) return

    mediaRecorder.onstop = async () => {
      await uploadExistingRecording()
    }

    mediaRecorder.stop()
    await cleanupStreams()
  }, [interviewId, mediaRecorder, uploadExistingRecording, cleanupStreams])

  const stopRecordingNoUpload = async () => {
    if (!mediaRecorder) return
    mediaRecorder.stop()
    await cleanupStreams()
  }

  const discardExistingRecording = async () => {
    if (!interviewId) return
    try {
      await deleteAudioChunks(interviewId)
      await cleanupStreams()
    } catch {
      showErrorToast('Failed to discard recording')
    }
  }

  useTabSharingEndedEffect(streams.tabStream, stopRecordingAndUpload)

  useEffect(() => {
    if (!isRecording) return

    const handleDeviceChange = () => {
      enumerateAudioDevices()
    }

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      )
    }
  }, [isRecording])

  return (
    <AudioRecorderContext
      value={{
        isRecording,
        sourceType,
        tabStream: streams.tabStream,
        visStream: streams.visStream,
        audioContext: streams.audioContext,
        recordedFile,
        availableDevices,
        selectedDeviceId,
        startMicrophoneRecording,
        startTabRecording,
        stopRecordingAndUpload,
        stopRecordingNoUpload,
        discardExistingRecording,
        uploadExistingRecording,
        switchMicrophone,
        mimeType,
        liveTranscriptSegments: liveTranscription.segments,
        liveTranscriptInterimSegment: liveTranscription.interimSegment,
        isLiveTranscriptionConnected: liveTranscription.isConnected,
        liveTranscriptionError: liveTranscription.error,
        selectedLiveTranscriptionProvider,
        setSelectedLiveTranscriptionProvider,
      }}
    >
      {children}
    </AudioRecorderContext>
  )
}

const useCleanChunksIfFullTranscriptIsGenerated = () => {
  const interview = useInterview()

  useEffect(() => {
    if (interview.data?.fullTranscript) {
      deleteAudioChunks(interview.data?.id)
    }
  }, [interview.data?.fullTranscript, interview.data?.id])
}

const useTabSharingEndedEffect = (
  tabStream: MediaStream | null,
  stopRecordingAndUpload: () => void,
) => {
  useEffect(() => {
    if (tabStream) {
      const videoTrack = tabStream.getVideoTracks()[0]
      const handleEnded = () => {
        console.log('Tab sharing ended, stopping recording.')
        stopRecordingAndUpload()
      }

      videoTrack.addEventListener('ended', handleEnded)
      return () => videoTrack.removeEventListener('ended', handleEnded)
    }
  }, [tabStream, stopRecordingAndUpload])
}

export function useAudioRecorder() {
  const ctx = use(AudioRecorderContext)
  if (!ctx)
    throw new Error(
      'useAudioRecorder must be used within <AudioRecorderProvider>',
    )
  return ctx
}
